import "./tour.css";
import ResizeObserver from 'resize-observer-polyfill';


function clamp(val: number, min: number, max: number) {
  return Math.max(Math.min(val, max), min);
}

class Tour {

  urlWithoutParams: string;
  onHide: () => void;
  steps: Step[] = [];
  element: HTMLElement;
  stepPosition: number = 0;
  previousStepPosition: number = -1;
  textBox: TextBox;
  grid: Grid;
  highlightClipArea: HTMLElement;
  closeButton: HTMLElement;

  constructor(onHide: () => void = function () { }) {
    const locstring = location.toString();
    this.urlWithoutParams = location.search.length > 0 ? locstring.substring(0, locstring.indexOf(location.search)) : locstring;
    this.onHide = onHide;
    this.element = document.createElement("div");
    this.element.className = "tourelement";
    this.element.style.cssText = "position: absolute; width: 100%; height: 100%; pointer-events: none; z-Index: 9999999; top: 0; overflow: hidden;";
    this.textBox = new TextBox(this.element);
    this.grid = new Grid();

    let observer = new ResizeObserver(() => {
      if (this.visible()) {
        this.renderStep(this.steps[this.stepPosition]);
      }
    });
    observer.observe(document.body);

    this.highlightClipArea = document.createElement("div");
    this.highlightClipArea.id = "highlightClipArea";
    this.element.appendChild(this.highlightClipArea);

    this.closeButton = document.createElement("div");
    this.closeButton.className = "closeButton";
    this.closeButton.innerHTML = "<i class='fas fa-times'></i>"
    this.closeButton.addEventListener("click", () => { this.hide(); });
    this.element.appendChild(this.closeButton);

    let last_known_scroll_position = 0;
    let ticking = false;

    let doSomething = (scroll_pos: number) => {
      if(this.visible())
        this.renderStep(this.steps[this.stepPosition]);
    }

    window.addEventListener('scroll', function (e) {
      let scrollTop = (e.target as HTMLElement).scrollTop;
      if (scrollTop !== last_known_scroll_position) {
        last_known_scroll_position = scrollTop;

        if (!ticking) {
          window.requestAnimationFrame(function () {
            doSomething(last_known_scroll_position);
            ticking = false;
          });

          ticking = true;
        }
      }
    }, true);

  }

  getButton(innerHTML: string, className: string) {
    let btn = document.createElement("button");
    btn.innerHTML = innerHTML;
    btn.classList.add(className);
    return btn;
  }

  getNextButton(innerHTML: string, className: string) {
    let btn = this.getButton(innerHTML, className);
    btn.classList.add("tour-next-button");
    btn.addEventListener("click", () => {
      this.nextStep();
    });
    return btn;
  }

  getSkipButton(innerHTML: string, className: string) {
    let btn = this.getButton(innerHTML, className);
    btn.classList.add("tour-skip-button");
    btn.addEventListener("click", () => {
      this.hide();
    });
    return btn;
  }

  getTourWasPlayed() {
    return localStorage.getItem(this.urlWithoutParams) === "true";
  }

  setTourWasPlayed() {
    localStorage.setItem( this.urlWithoutParams, "true");
  }

  start() {
    this.show();
    this.stepPosition = -1;
    this.previousStepPosition = -1;
    this.nextStep();
  }

  addStep(step: Step) {
    this.steps.push(step);
  }

  nextStep() {
    do {
      this.previousStepPosition = this.stepPosition;
      this.stepPosition++;

      if (this.stepPosition >= this.steps.length) {
        this.hide();
        return;
      }
      else {
        if (this.previousStepPosition >= 0 && !this.steps[this.previousStepPosition].skipStep()) {
          this.steps[this.previousStepPosition].onAfterStep();
        }
        if (!this.steps[this.stepPosition].skipStep()) {
          this.steps[this.stepPosition].onBeforeStep();
          this.renderStep(this.steps[this.stepPosition]);
        }
      }
    } while (this.stepPosition < this.steps.length && this.steps[this.stepPosition].skipStep());
  }

  show() {
    if (!this.element.parentElement) {
      document.body.appendChild(this.element);
    }
  }

  visible() {
    return !!this.element.parentElement;
  }

  hide() {
    if(this.stepPosition < this.steps.length)
      this.steps[this.stepPosition].onAfterStep();
    this.setTourWasPlayed();
    if(this.element.parentElement)
      this.element.remove();
  }

  getCurveOffset(direction: StepPosition, width: number, height: number) {
    let result = { x: 0, y: 0 };
    let mincurve = 100;
    if (direction === "top") {
      result.y = Math.min(-mincurve ,-height / 2);
    }
    else if (direction === "bottom") {
      result.y = Math.max(mincurve ,height / 2);
    }
    else if (direction === "left") {
      result.x = Math.min(-mincurve ,-width / 2);
    }
    else if (direction === "right") {
      result.x = Math.max(mincurve ,width / 2);
    }
    return result;
  }

  renderStep(step: Step) {
    step.onRenderStep();


    let innerpath = "";
    let arrow = "";
    let fill = "rgba(0,0,0,0.6)";

    let clip = this.highlightClipArea;

    if (step.position && step.position.textBox && step.position.textBox.position) {
      this.textBox.setText(step.text);
      let boxPosition = this.grid.getPosition(step.position.textBox.position, this.textBox.getSize());
      this.textBox.setPosition(boxPosition.x, boxPosition.y);
      this.textBox.clearButtons();
      if (step.buttons && Array.isArray(step.buttons)) {
        this.textBox.setButtons(step.buttonBox);
      }
      this.textBox.show();
    }
    else {
      this.textBox.clearButtons();
      this.textBox.hide();
    }

    if (step.anchorElement) {
      let radius = 5;
      let stepAnchorElement: HTMLElement | null = document.querySelector(step.anchorElement);
      if (!stepAnchorElement) {
        return;
      }
      let padding = 10;
      let boundingRect = stepAnchorElement.getBoundingClientRect();
      let x = boundingRect.left - (padding / 2);
      let y = boundingRect.top - (padding / 2);
      let width = (boundingRect.right - boundingRect.left) + padding;
      let height = (boundingRect.bottom - boundingRect.top) + padding;
      let right = boundingRect.right + (padding / 2);
      let bottom = boundingRect.bottom + (padding / 2);

      innerpath = `M ${x},${y + radius}
      L ${x},${bottom - radius}
      Q ${x},${bottom} ${x + radius},${bottom}
      L ${right - radius}, ${bottom}
      Q ${right},${bottom} ${right},${bottom - radius}
      L ${right},${y + radius}
      Q ${right},${y} ${right - radius},${y}
      L ${x + radius},${y}
      Q ${x},${y} ${x},${y + radius} Z`;



      if ( step.position && step.position.anchor && step.position.textBox && step.position.textBox.anchor && step.position.textBox.position) {
        let textAnchor = getAnchor(this.textBox.element, step.position.textBox.anchor, 10);
        let anchor = getAnchor(stepAnchorElement, step.position.anchor, 20);

        let connectionWidth = Math.abs(textAnchor.x - anchor.x);
        let connectionHeight = Math.abs(textAnchor.y - anchor.y);

        let anchorCurveOffset = this.getCurveOffset(step.position.anchor, connectionWidth, connectionHeight);
        let textAnchorCurveOffset = this.getCurveOffset(step.position.textBox.anchor, connectionWidth, connectionHeight);

        let angle = Math.atan2(anchorCurveOffset.y, anchorCurveOffset.x) / Math.PI * 180.0;
        // let angle = Math.atan2(textAnchorCurveOffset.y, textAnchorCurveOffset.x)/Math.PI * 360.0;
        arrow = `
          <path d="
          M ${textAnchor.x},${textAnchor.y}
          C ${textAnchor.x + textAnchorCurveOffset.x},${textAnchor.y + textAnchorCurveOffset.y} ${anchor.x + anchorCurveOffset.x},${anchor.y + anchorCurveOffset.y} ${anchor.x},${anchor.y}"
          fill="none" stroke="white" stroke-width="3"/>
          <use xlink:href="#arrowmarker_" stroke-width="2" vector-effect="non-scaling-stroke" transform="translate(${anchor.x -10} ${anchor.y - 10}) rotate(${angle-180} 10 10) scale(2)" />
          `;
        }
      }



      clip.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" style="width: 100%; height: 100%; position:absolute; pointer-events: none;">
      <defs>
        <marker id="arrowmarker" viewBox="0 0 10 10" refX="5" refY="5"
            markerWidth="10" markerHeight="10"
            orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10" stroke="white" fill="none"/>
        </marker>
        <path id="arrowmarker_" d="M 0 0 L 10 5 L 0 10" stroke="white" fill="none" vector-effect="non-scaling-stroke"/>
      </defs>
      <path fill="${fill}" pointer-events="visibleFill" d="M 0,0 L ${window.innerWidth},0 ${window.innerWidth},${window.innerHeight} 0,${window.innerHeight} Z
      ${innerpath}
      "/>

      ${arrow}
    </svg>`.toString();
  }
}



function getAnchor(element: HTMLElement, direction: "top" | "bottom" | "left" | "right", margin = 0) {
  let rect = element.getBoundingClientRect();
  let result = { x: 0, y: 0 };
  if (direction === "top") {
    result.x = (rect.right + rect.left) / 2;
    result.y = rect.top - margin;
  }
  else if (direction === "bottom") {
    result.x = (rect.right + rect.left) / 2;
    result.y = rect.bottom + margin;
  }
  else if (direction === "right") {
    result.x = rect.right + margin;
    result.y = (rect.bottom + rect.top) / 2;
  }
  else if (direction === "left") {
    result.x = rect.left - margin;
    result.y = (rect.bottom + rect.top) / 2;
  }
  return result;
}

type GridPosition = "tl" | "cl" | "bl" | "tc" | "cc" | "bc" | "tr" | "cr" | "br";
interface size {
  width: number;
  height: number
}

class Grid {
  borderMargin: number;
  grid: Record<GridPosition, { x: number, y: number }>;
  constructor() {
    this.borderMargin = 0.05;
    this.grid = {
      "tl": {
        x: this.borderMargin,
        y: this.borderMargin
      },
      "cl": {
        x: this.borderMargin,
        y: 0.5
      },
      "bl": {
        x: this.borderMargin,
        y: 1 - this.borderMargin
      },
      "tc": {
        x: 0.5,
        y: this.borderMargin
      },
      "cc": {
        x: 0.5,
        y: 0.5
      },
      "bc": {
        x: 0.5,
        y: 1 - this.borderMargin
      },
      "tr": {
        x: 1 - this.borderMargin,
        y: this.borderMargin
      },
      "cr": {
        x: 1 - this.borderMargin,
        y: 0.5
      },
      "br": {
        x: 1 - this.borderMargin,
        y: 1 - this.borderMargin
      }
    }
  }

  getPosition( position: GridPosition, size: size) {
    let relativePos = this.grid[position];
    let targetX = window.innerWidth * relativePos.x;
    let targetY = window.innerHeight * relativePos.y;
    let boxX = targetX - size.width / 2;
    let boxY = targetY - size.height / 2;
    let marginX = this.borderMargin * window.innerWidth;
    let marginY = this.borderMargin * window.innerHeight;

    boxX = clamp(boxX, marginX, screen.width - size.width - marginX);
    boxY = clamp(boxY, marginY, screen.height - size.height - marginY);
    return { x: boxX, y: boxY };
  }
}


class TextBox{
  
  element: HTMLElement;
  textArea: HTMLElement;
  buttonsArea: HTMLElement;
  visible: boolean;

  constructor(parent: HTMLElement) {
    this.element = document.createElement("div");
    this.element.className = "tour-text-area";
    this.element.style.cssText = "position: absolute; display:none; z-index:9999; color: white; max-width: min(600px, 100vw); pointer-events: all;";
    parent.appendChild(this.element);
    this.visible = false;
    this.textArea = document.createElement("div");
    this.element.appendChild(this.textArea);
    this.buttonsArea = document.createElement("div");
    this.buttonsArea.className = "tour-buttons-area";
    this.element.appendChild(this.buttonsArea);
  }

  setText(text: string | undefined) {
    if (text) {
      this.textArea.innerHTML = text;
    }
    else {
      this.textArea.innerHTML = "";
    }
  }

  setPosition(x: number, y: number) {
    this.element.style.left = x + "px";
    this.element.style.top = y + "px";
  }

  show() {
    this.element.style.display = "inline-block";
    this.visible = true;
  }

  hide() {
    this.element.style.display = "none";
    this.visible = false;
  }

  clearButtons() {
    this.setButtons(this.buttonsArea);
    // this.buttonsArea.innerHTML = "";
  }

  appendButtons(buttons: Array<HTMLButtonElement>) {
    for (let button of buttons) {
      this.buttonsArea.appendChild(button);
    }
  }
  
  setButtons(buttonsbox: HTMLElement) {
    this.element.replaceChild(buttonsbox, document.querySelector(".tour-buttons-area")!);
  }

  /**
   * @returns {size}
   */
  getSize() {
    if (!this.visible) {
      this.element.style.visibility = "hidden";
      this.element.style.display = "inline-block";
    }
    let rect = this.element.getBoundingClientRect();
    if (!this.visible) {
      this.element.style.display = "none";
      this.element.style.removeProperty("visibility");
    }
    return { width: rect.right - rect.left, height: rect.bottom - rect.top }
  }
}




type StepPosition = "top" | "bottom" | "left" | "right";

interface TextBoxOptions {
  position: GridPosition;
  anchor?: StepPosition;
}

interface PositionOptions {
  textBox: TextBoxOptions;
  anchor?: StepPosition;
}


interface StepOptions {
  anchorElement?: string;
  position?: PositionOptions;
  text?: string;
  onBeforeStep?: () => void;
  onRenderStep?: () => void;
  onAfterStep?: () => void;
  skipStepIf?: () => boolean;
  buttons?: Array<HTMLButtonElement>;
}

class Step {
  anchorElement: string | undefined;
  position: PositionOptions | undefined;
  text: string | undefined;
  onBeforeStep: () => void;
  onRenderStep: () => void;
  onAfterStep: () => void;  
  skipStepIf: () => boolean;
  buttons: Array<HTMLButtonElement> | undefined;
  buttonBox: HTMLElement;

  constructor(stepOptions: StepOptions) {
    this.anchorElement = stepOptions.anchorElement;
    this.text = stepOptions.text;
    this.position = stepOptions.position;
    this.buttons = stepOptions.buttons;
    this.onBeforeStep = stepOptions.onBeforeStep || function () { };
    this.onRenderStep = stepOptions.onRenderStep || function () { };
    this.onAfterStep = stepOptions.onAfterStep || function () { };
    this.skipStepIf = stepOptions.skipStepIf || function () { return false };
    this.buttonBox = this.getButtonsBox();
  }

  skipStep() {
    return this.skipStepIf() || !this.stepAnchorIsVisible();
  }

  stepAnchorIsVisible() {
    if (!this.anchorElement) {
      return true;
    }
    let elem: HTMLElement | null = document.querySelector(this.anchorElement);
    /* https://stackoverflow.com/a/21696585
    Offset-Parent ist null, wenn Element fixed positioniert oder nicht sichtbar
     */
    return elem && elem.offsetParent !== null;
  }

  getButtonsBox() {
    let box = document.createElement("div");
    box.className = "tour-buttons-area";
    if (Array.isArray(this.buttons)) {
      this.buttons.forEach((btn) => {
        box.appendChild(btn);
      });
    }
    return box;
  }
}


interface ClickArea {
  top: string;
  left: string;
  text?: string;
  height: string;
  width: string;
}

export function getClickArea(opts: ClickArea= { top: "60%", left: "50%", text: undefined, height: "50%", width: "70%" }) {
  const clickArea = document.createElement("div");
  clickArea.id = "clickArea";
  clickArea.className = "highlightmask";
  clickArea.style.cssText = `border-radius: 5px; position: absolute; top: ${opts.top}; left: ${opts.left}; transform: translate(-50%, -50%); width: ${opts.width}; height: ${opts.height}; pointer-events: none; display: flex; justify-content: center; align-items: center; text-align:center;`;
  if(opts.text){
    clickArea.innerHTML = `<h2 style='color: white; text-shadow: 0 0 3px black, 0 0 3px black, 0 0 3px black, 0 0 3px black;'>${opts.text}</h2>`;
  }
  return clickArea;
}

export {
  Tour,
  Step
}